// Cotas del enganche del cotizador: las del plan, pisadas por la última cotización o por un rechazo.
import { useEffect, useState } from 'react';
import type {
    StoreQuoterPlan,
    StoreQuoterSimulation,
} from '@/api/services/loan-offerings/quoter.contract';
import {
    learnDownPaymentPcts,
    noFeasibleDownPayment,
    type DownPaymentPcts,
} from '@/domains/sales/wizard/downPaymentBounds';
import { noFeasibleDownPaymentMessage } from '@/domains/sales/wizard/quoteErrors';
import { downPaymentBounds, downPaymentPctsOf, type DownPaymentBounds } from './lib/quoterPlans';

interface EffectiveLimits {
    key: string;
    signature: string;
    terms: StoreQuoterSimulation['effectiveTerms'];
}

interface LearnedPcts {
    key: string;
    pcts: DownPaymentPcts;
}

export interface DownPaymentLimits {
    key: string;
    pcts: DownPaymentPcts | null;
    bounds: DownPaymentBounds | null;
    /** Aviso del wizard cuando el mínimo pasa del máximo; ahí no se cotiza. */
    noFeasible: string | null;
    effective: EffectiveLimits | null;
    setEffective: (value: EffectiveLimits) => void;
    setLearned: (value: LearnedPcts | null) => void;
}

/** Mismo aviso del wizard; el tope financiable sale del piso efectivo, como allá. */
function noFeasibleMessage(priceCents: number, pcts: DownPaymentPcts): string {
    const cap = pcts.minPct === null ? undefined : Math.round(priceCents * (1 - pcts.minPct));
    return noFeasibleDownPaymentMessage(priceCents, cap);
}

export function useDownPaymentLimits(
    plan: StoreQuoterPlan | null,
    priceCents: number,
): DownPaymentLimits {
    const [effective, setEffective] = useState<EffectiveLimits | null>(null);
    const [learned, setLearned] = useState<LearnedPcts | null>(null);
    const key = `${plan?.planId ?? ''}|${String(priceCents)}`;
    const limits = effective?.key === key ? effective.terms : plan;
    const pcts =
        learned?.key === key ? learned.pcts : limits && downPaymentPctsOf(limits, priceCents);
    const noFeasible =
        pcts && priceCents > 0 && noFeasibleDownPayment(pcts)
            ? noFeasibleMessage(priceCents, pcts)
            : null;
    const bounds =
        plan && pcts && priceCents > 0 && noFeasible === null
            ? downPaymentBounds(priceCents, plan, pcts)
            : null;
    return { key, pcts, bounds, noFeasible, effective, setEffective, setLearned };
}

interface QuoteFeedback {
    priceCents: number;
    requestPrice: number;
    quote: StoreQuoterSimulation | null;
    rejection: unknown;
}

/** Guarda las cotas que revela la cotización (o su rechazo, como el wizard) para el próximo render. */
export function useRecordQuoteLimits(limits: DownPaymentLimits, feedback: QuoteFeedback): void {
    const { priceCents, requestPrice, quote, rejection } = feedback;
    const { key, pcts, effective, setEffective, setLearned } = limits;

    useEffect(() => {
        const signature =
            quote && quote.productPriceCents === priceCents
                ? `${key}|${JSON.stringify(quote.effectiveTerms)}`
                : null;
        if (quote && signature !== null && signature !== effective?.signature) {
            setEffective({ key, signature, terms: quote.effectiveTerms });
            setLearned(null);
            return;
        }
        if (!rejection || !pcts || requestPrice !== priceCents) return;
        const learned = learnDownPaymentPcts(pcts, rejection, priceCents);
        if (JSON.stringify(learned) !== JSON.stringify(pcts)) setLearned({ key, pcts: learned });
    }, [
        key,
        pcts,
        effective,
        quote,
        rejection,
        requestPrice,
        priceCents,
        setEffective,
        setLearned,
    ]);
}
